import { useEffect, useLayoutEffect, useRef } from 'react';

import { FormSpy } from 'react-final-form';

import {
  useDrawerInstance,
  useDrawerOverlay,
  ExitConfirmationOverlay,
} from '@cast/design-system';

type RouteBlocker = {
  state: string;
  proceed?: () => void;
  reset?: () => void;
};

type Props = {
  isDirty?: boolean;
  formGuard?: boolean;
  onLeave?: () => void;
  onContinue?: () => void;
  blocker?: RouteBlocker;
  onFormDirtyChange?: (dirty: boolean) => void;
};

export const CloseDrawerGuard = ({
  isDirty: externalIsDirty = false,
  formGuard = true,
  onLeave,
  onContinue,
  blocker,
  onFormDirtyChange,
}: Props) => {
  const { shouldRenderGuard, setShouldRenderGuard, setOnCloseGuard, close } =
    useDrawerInstance();
  const isDirty = externalIsDirty;

  const onLeaveRef = useRef(onLeave);
  const onContinueRef = useRef(onContinue);
  useLayoutEffect(() => {
    onLeaveRef.current = onLeave;
    onContinueRef.current = onContinue;
  });

  useEffect(() => {
    setOnCloseGuard(isDirty);
  }, [isDirty, setOnCloseGuard]);

  const routeGuard = blocker;
  const { showOverlay, destroyOverlay, activeOverlayId } = useDrawerOverlay();

  useEffect(() => {
    if (shouldRenderGuard && activeOverlayId !== 'leave-guard') {
      showOverlay({
        id: 'leave-guard',
        content: (
          <ExitConfirmationOverlay
            onLeave={() => {
              if (routeGuard?.state === 'blocked') {
                routeGuard.proceed?.();
              } else {
                close();
              }
              onLeaveRef.current?.();
            }}
            onContinue={() => {
              if (routeGuard?.state === 'blocked') {
                routeGuard.reset?.();
              }
              setShouldRenderGuard(false);
              destroyOverlay();
              onContinueRef.current?.();
            }}
          />
        ),
      });
    }
  }, [
    shouldRenderGuard,
    activeOverlayId,
    routeGuard,
    destroyOverlay,
    showOverlay,
    setShouldRenderGuard,
    close,
  ]);

  if (!formGuard) {
    return null;
  }

  return (
    <FormSpy
      subscription={{ dirty: true }}
      onChange={({ dirty }) => {
        onFormDirtyChange?.(dirty);
      }}
    />
  );
};
